import { useState, useCallback } from 'react'
import { useQueryClient, useQuery } from '@tanstack/react-query'
import { Cart } from '../../../../client/types/cart'
import { getCart } from '../../../../client/api/cart'
import { getCartQueryKey } from '../../../../client/utils/cart-helpers'
import { NamespacedStorage } from '../../../../client/utils/namespaced-storage'

interface UseCartPersistenceProps {
  restaurantStorage: NamespacedStorage
  fastLinkName: string | undefined
  setOptedInTo3pAccountLookup: (value: boolean) => void
}

export const useCartPersistence = ({
  restaurantStorage,
  fastLinkName,
  setOptedInTo3pAccountLookup
}: UseCartPersistenceProps) => {
  const queryClient = useQueryClient()

  const getCartGuid = useCallback(() => {
    const cached = restaurantStorage.get('cartGuid')
    if (cached && cached['value']) {
      return cached.value
    } else {
      return cached
    }
  }, [restaurantStorage])

  const [cartGuid, setCartGuid] = useState(getCartGuid())

  const refreshCartGuid = useCallback(() => {
    setCartGuid(getCartGuid())
  }, [getCartGuid])

  const getOtherCachedCartGuid = useCallback(
    (linkName: string | undefined) => {
      return restaurantStorage.get(`cart:${linkName || 'default'}`)
    },
    [restaurantStorage]
  )

  // Use React Query to fetch cart data
  const {
    data: cart,
    isInitialLoading: isLoading,
    isFetching,
    isError,
    error: networkError,
    refetch
  } = useQuery<Cart, any>({
    queryKey: getCartQueryKey(cartGuid),
    queryFn: () => getCart(cartGuid!!, fastLinkName),
    staleTime: 60 * 1000,
    enabled: !!cartGuid,
    retry: (failureCount, error) => {
      // Don't retry if cart doesn't exist (404)
      if (error?.status === 404) {
        return false
      }
      // Otherwise, use default retry behavior (3 attempts)
      return failureCount < 3
    }
  })

  const updateCartGuid = useCallback(
    (newCartGuid: string, linkName: string | undefined) => {
      if (newCartGuid !== cartGuid) {
        restaurantStorage.set('cartGuid', {
          value: newCartGuid
        })

        // where the cart was last used
        restaurantStorage.set(`cart:${linkName || 'default'}`, newCartGuid)

        refreshCartGuid()
      }
    },
    [cartGuid, restaurantStorage, refreshCartGuid]
  )

  const updateCartCache = useCallback(
    (response: Cart) => {
      queryClient.setQueryData(getCartQueryKey(response.guid), response)
      updateCartGuid(response.guid, response.metadata?.fastLinkName)
    },
    [queryClient, updateCartGuid]
  )

  const deleteCartGuid = useCallback(
    (linkName: string | undefined) => {
      if (cartGuid) {
        restaurantStorage.remove('cartGuid')
        restaurantStorage.remove(`cart:${linkName || 'default'}`)

        refreshCartGuid()
        setOptedInTo3pAccountLookup(false)
      }
    },
    [cartGuid, restaurantStorage, refreshCartGuid, setOptedInTo3pAccountLookup]
  )

  // Handle cart cleanup scenarios
  // Scenario 1: API returns 404 (cart not found on server)
  let error = networkError
  if (networkError?.status === 404) {
    error = null
    deleteCartGuid(fastLinkName)
  }

  // Scenario 2: Response contains no cart guid
  if (!isLoading && !isError && !cart?.guid) {
    deleteCartGuid(fastLinkName)
  }

  return {
    cartGuid,
    cart,
    isFetching,
    error,
    refetch,
    updateCartGuid,
    updateCartCache,
    deleteCartGuid,
    getOtherCachedCartGuid
  }
}
